import React, { useCallback } from 'react';

import { cssMap } from '@atlaskit/css';
import type { Transaction } from '@atlaskit/editor-prosemirror/state';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';
import ButtonItem from '@atlaskit/menu/button-item';
import { Box, Inline, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import type { INPUT_METHOD } from '../analytics';
import type { TypeAheadInsert } from '../types';

import { useQuickInsertContext } from './useQuickInsertContext';

const styles = cssMap({
	icon: {
		alignItems: 'center',
		backgroundColor: token('elevation.surface.overlay'),
		borderColor: token('color.border'),
		borderRadius: token('radius.medium'),
		borderStyle: 'solid',
		borderWidth: token('border.width'),
		display: 'flex',
		height: '32px',
		justifyContent: 'center',
		overflow: 'hidden',
		width: '32px',
	},
	shortcut: {
		backgroundColor: token('color.background.neutral'),
		borderRadius: token('radius.small'),
		color: token('color.text.subtle'),
		font: token('font.body.small'),
		paddingBlock: token('space.025'),
		paddingInline: token('space.050'),
	},
});

type Props = {
	ariaLabel?: string;
	iconBefore?: React.ReactNode;
	isDisabled?: boolean;
	onSelect: (context: OnSelectContext) => Transaction | false | void;
	shortcut?: string;
	shouldWrapIcon?: boolean;
	title: string;
};

export type OnSelectContext = {
	editorView: EditorView;
	insert: TypeAheadInsert;
	source: INPUT_METHOD.ELEMENT_BROWSER | INPUT_METHOD.QUICK_INSERT | INPUT_METHOD.TOOLBAR;
};

export const QuickInsertMenuItem = ({
	ariaLabel,
	iconBefore,
	isDisabled,
	onSelect,
	shortcut,
	shouldWrapIcon = true,
	title,
}: Props): React.JSX.Element => {
	const { editorView, item, select } = useQuickInsertContext();
	const isSelected = item?.isSelected ?? false;
	const handleClick = useCallback(
		() =>
			select((selectionContext) =>
				onSelect({
					editorView,
					insert: selectionContext.insert,
					source: selectionContext.source,
				}),
			),
		[editorView, onSelect, select],
	);

	return (
		<ButtonItem
			aria-label={ariaLabel}
			aria-selected={isSelected}
			iconBefore={
				iconBefore && shouldWrapIcon ? <Box xcss={styles.icon}>{iconBefore}</Box> : iconBefore
			}
			id={item?.id}
			isDisabled={isDisabled}
			isSelected={isSelected}
			onClick={handleClick}
			role="option"
		>
			<Inline alignBlock="center" spread="space-between">
				<Text>{title}</Text>
				{shortcut && (
					<Box as="span" xcss={styles.shortcut}>
						{shortcut}
					</Box>
				)}
			</Inline>
		</ButtonItem>
	);
};
